import { useState, useEffect, useMemo } from 'react'
import { supabase } from '../supabase'
import { useToast } from '../hooks/useToast'

const STATUS_LABEL = { orcamento: 'Orçamento', confirmado: 'Confirmado', entregue: 'Entregue' }
const STATUS_COLOR = { orcamento: '#ff9800', confirmado: '#4caf50', entregue: '#9c27b0' }
const PAG_LABEL = { pix: 'PIX', dinheiro: 'Dinheiro', cartao: 'Cartão' }

function pp(str) { return parseFloat(String(str || '').replace(',', '.')) || 0 }

function fmtData(ts) {
  if (!ts) return '—'
  return new Date(ts).toLocaleDateString('pt-BR')
}

function calcCustoComp(itensList, cestosList) {
  const custItens = itensList.reduce((s, i) => s + i.item.custo * i.qty, 0)
  const custCestos = cestosList.reduce((s, c) => s + c.cesto.custo * c.qty, 0)
  const maoDeObra = itensList.reduce((s, i) => s + i.item.custo * i.qty * ((i.item.margem ?? 10) / 100), 0)
  const montagem = 15 + maoDeObra
  return { custItens, custCestos, montagem, total: custItens + custCestos + montagem }
}

export default function Pedidos() {
  const [pedidos, setPedidos] = useState([])
  const [arranjos, setArranjos] = useState([])
  const [itens, setItens] = useState([])
  const [cestos, setCestos] = useState([])
  const [loading, setLoading] = useState(true)

  const [view, setView] = useState('lista')
  const [viewPedido, setViewPedido] = useState(null)
  const [filtroStatus, setFiltroStatus] = useState('')

  // novo pedido
  const [step, setStep] = useState('produtos')
  const [itensPedido, setItensPedido] = useState([])
  const [editingItem, setEditingItem] = useState(null)
  const [buscaArr, setBuscaArr] = useState('')

  // builder
  const [bNome, setBNome] = useState('')
  const [bItens, setBItens] = useState([])
  const [bCestos, setBCestos] = useState([])
  const [bBusca, setBBusca] = useState('')
  const [bCat, setBCat] = useState('')
  const [bPreco, setBPreco] = useState('')

  // dados cliente
  const [cliNome, setCliNome] = useState('')
  const [cliWhats, setCliWhats] = useState('')
  const [pagamento, setPagamento] = useState('pix')
  const [frete, setFrete] = useState('')
  const [desconto, setDesconto] = useState('')
  const [dataEntrega, setDataEntrega] = useState('')
  const [observacao, setObservacao] = useState('')
  const [recado, setRecado] = useState('')

  const [saving, setSaving] = useState(false)
  const [confirm, setConfirm] = useState(null)
  const [avisoEstoque, setAvisoEstoque] = useState(null)
  const [fichaOpen, setFichaOpen] = useState(false)
  const { msg, visible, show } = useToast()

  useEffect(() => { fetchTudo() }, [])

  async function fetchTudo() {
    setLoading(true)
    const [{ data: ped }, { data: arr }, { data: it }, { data: cs }] = await Promise.all([
      supabase.from('pedidos')
        .select('*, pedido_itens(*, pedido_item_composicao(*, itens(*), cestos(*)))')
        .order('created_at', { ascending: false }),
      supabase.from('arranjos')
        .select('*, arranjo_itens(*, itens(*)), arranjo_cestos(*, cestos(*, cesto_itens(*, itens(*))))')
        .order('nome'),
      supabase.from('itens').select('*').order('categoria').order('nome'),
      supabase.from('cestos').select('*, cesto_itens(*, itens(*))').order('nome'),
    ])
    setPedidos(ped || [])
    setArranjos(arr || [])
    setItens(it || [])
    setCestos(cs || [])
    setLoading(false)
  }

  function resetNovo() {
    setStep('produtos')
    setItensPedido([])
    setEditingItem(null)
    setCliNome(''); setCliWhats(''); setPagamento('pix')
    setFrete(''); setDesconto(''); setDataEntrega('')
    setObservacao(''); setRecado(''); setBuscaArr('')
  }

  // ---- CATÁLOGO ----
  function addCatalogo(arr) {
    const exists = itensPedido.find(i => i.tipo === 'catalogo' && i.arranjoId === arr.id)
    if (exists) {
      setItensPedido(prev => prev.map(i => i.arranjoId === arr.id ? { ...i, qty: i.qty + 1 } : i))
    } else {
      setItensPedido(prev => [...prev, {
        id: crypto.randomUUID(),
        tipo: 'catalogo',
        arranjoId: arr.id,
        arranjo: arr,
        nome: arr.nome,
        qty: 1,
        custoTotal: parseFloat(arr.custo_total || 0),
        precoInput: parseFloat(arr.preco_venda).toFixed(2),
      }])
    }
    setBuscaArr('')
  }

  // ---- BUILDER ----
  function abrirBuilder(editId = null) {
    if (editId) {
      const item = itensPedido.find(i => i.id === editId)
      if (item) {
        if (item.tipo === 'catalogo' && item.arranjo) {
          const itensDoArranjo = []
          for (const ai of (item.arranjo.arranjo_itens || [])) {
            if (ai.itens) itensDoArranjo.push({ item: ai.itens, qty: ai.quantidade })
          }
          for (const ac of (item.arranjo.arranjo_cestos || [])) {
            for (const ci of (ac.cestos?.cesto_itens || [])) {
              if (ci.itens) itensDoArranjo.push({ item: ci.itens, qty: ci.quantidade * ac.quantidade })
            }
          }
          setBNome(item.nome)
          setBItens(itensDoArranjo)
          setBCestos([])
        } else {
          setBNome(item.nome)
          setBItens(item.bItens || [])
          setBCestos(item.bCestos || [])
        }
        setBPreco(item.precoInput)
      }
    } else {
      setBNome(''); setBItens([]); setBCestos([]); setBPreco('')
    }
    setBBusca(''); setBCat('')
    setEditingItem(editId || 'novo')
  }

  function toggleBItem(item) {
    const e = bItens.find(s => s.item.id === item.id)
    if (e) setBItens(bItens.filter(s => s.item.id !== item.id))
    else setBItens([...bItens, { item, qty: 1 }])
  }

  function toggleBCesto(cesto) {
    const e = bCestos.find(s => s.cesto.id === cesto.id)
    if (e) setBCestos(bCestos.filter(s => s.cesto.id !== cesto.id))
    else setBCestos([...bCestos, { cesto, qty: 1 }])
  }

  function setBItemQty(id, qty) {
    if (qty < 1) return
    setBItens(bItens.map(s => s.item.id === id ? { ...s, qty } : s))
  }

  function setBCestoQty(id, qty) {
    if (qty < 1) return
    setBCestos(bCestos.map(s => s.cesto.id === id ? { ...s, qty } : s))
  }

  const builderCusto = useMemo(() => calcCustoComp(bItens, bCestos), [bItens, bCestos])

  function salvarBuilder() {
    if (!bNome.trim()) { show('Dê um nome ao item'); return }
    const preco = pp(bPreco)
    if (preco <= 0) { show('Defina o preço de venda'); return }

    const novoItem = {
      id: typeof editingItem === 'string' && editingItem !== 'novo' ? editingItem : crypto.randomUUID(),
      tipo: 'personalizado',
      nome: bNome.trim(),
      qty: 1,
      custoTotal: builderCusto.total,
      precoInput: preco.toFixed(2),
      bItens: [...bItens],
      bCestos: [...bCestos],
    }

    if (typeof editingItem === 'string' && editingItem !== 'novo') {
      setItensPedido(prev => prev.map(i => i.id === editingItem ? novoItem : i))
    } else {
      setItensPedido(prev => [...prev, novoItem])
    }
    setEditingItem(null)
  }

  function updateItem(id, field, value) {
    setItensPedido(prev => prev.map(i => i.id === id ? { ...i, [field]: value } : i))
  }

  // ---- TOTAIS ----
  const subtotal = itensPedido.reduce((s, i) => s + pp(i.precoInput) * (i.qty || 1), 0)
  const freteVal = pp(frete)
  const descontoVal = pp(desconto)
  const totalPedido = Math.max(0, subtotal + freteVal - descontoVal)
  const custoTotalPedido = itensPedido.reduce((s, i) => s + i.custoTotal * (i.qty || 1), 0)

  // ---- CHECAR ESTOQUE (síncrono, usa state) ----
  function baixasDeItensLocal(itensLocal) {
    const baixas = []
    for (const item of itensLocal) {
      const qty = item.qty || 1
      if (item.bItens?.length > 0 || item.tipo === 'personalizado') {
        // usa snapshot armazenado (catálogo confirmado depois ou personalizado)
        for (const s of (item.bItens || []))
          if (s.item?.movimenta_estoque !== false)
            baixas.push({ itemId: s.item.id, nome: s.item.nome, qty: s.qty * qty })
        // cestos do personalizado ainda precisam ser expandidos
        for (const { cesto, qty: qCesto } of (item.bCestos || []).map(s => ({ cesto: s.cesto, qty: s.qty * qty })))
          for (const ci of (cesto?.cesto_itens || []))
            if (ci.itens?.movimenta_estoque !== false)
              baixas.push({ itemId: ci.item_id, nome: ci.itens?.nome || '?', qty: ci.quantidade * qCesto })
      } else if (item.tipo === 'catalogo' && item.arranjo) {
        // pedido novo ainda em memória — usa estado ao vivo (é o snapshot do momento do save)
        for (const ai of (item.arranjo.arranjo_itens || []))
          if (ai.itens?.movimenta_estoque !== false)
            baixas.push({ itemId: ai.item_id, nome: ai.itens?.nome || '?', qty: ai.quantidade * qty })
        for (const ac of (item.arranjo.arranjo_cestos || []))
          for (const ci of (ac.cestos?.cesto_itens || []))
            if (ci.itens?.movimenta_estoque !== false)
              baixas.push({ itemId: ci.item_id, nome: ci.itens?.nome || '?', qty: ci.quantidade * ac.quantidade * qty })
      }
    }
    return baixas
  }

  function checarEstoque(itensLocal) {
    const mapa = {}
    for (const { itemId, nome, qty: q } of baixasDeItensLocal(itensLocal)) {
      if (!mapa[itemId]) mapa[itemId] = { nome, necessario: 0, disponivel: parseFloat(itens.find(i => i.id === itemId)?.estoque || 0) }
      mapa[itemId].necessario += q
    }
    return Object.values(mapa).filter(m => m.disponivel < m.necessario)
  }

  // ---- SALVAR ----
  async function salvarPedido(status, forcar = false) {
    if (itensPedido.length === 0) { show('Adicione pelo menos um produto'); return }
    if (status === 'confirmado' && !forcar) {
      const falta = checarEstoque(itensPedido)
      if (falta.length > 0) {
        setAvisoEstoque({ falta, onConfirmar: () => { setAvisoEstoque(null); salvarPedido(status, true) } })
        return
      }
    }
    setSaving(true)

    const statusInicial = status === 'confirmado' ? 'orcamento' : status
    const { data: pedido, error } = await supabase.from('pedidos').insert([{
      cliente_nome: cliNome.trim() || null,
      cliente_whats: cliWhats.trim() || null,
      pagamento,
      frete: freteVal,
      desconto: descontoVal,
      total: totalPedido,
      status: statusInicial,
      data_entrega: dataEntrega || null,
      observacao: observacao.trim() || null,
      recado: recado.trim() || null,
    }]).select().single()

    if (error) { show('Erro ao salvar pedido'); setSaving(false); return }

    for (const item of itensPedido) {
      const { data: pi, error: itemError } = await supabase.from('pedido_itens').insert([{
        pedido_id: pedido.id,
        tipo: item.tipo,
        arranjo_id: item.tipo === 'catalogo' ? item.arranjoId : null,
        nome: item.nome,
        quantidade: item.qty,
        custo_total: item.custoTotal,
        preco_venda: pp(item.precoInput),
      }]).select().single()

      if (itemError || !pi) {
        await supabase.from('pedidos').delete().eq('id', pedido.id)
        show('Erro ao salvar itens do pedido')
        setSaving(false)
        return
      }

      if (pi) {
        const comps = []
        if (item.tipo === 'catalogo' && item.arranjo) {
          // snapshot dos itens diretos do arranjo
          for (const ai of (item.arranjo.arranjo_itens || []))
            comps.push({ pedido_item_id: pi.id, item_id: ai.item_id, quantidade: ai.quantidade, custo_unitario: ai.itens?.custo ?? ai.custo_unitario, tipo_comp: 'item' })
          // snapshot dos itens dentro de cada cesto do arranjo (já expandidos)
          for (const ac of (item.arranjo.arranjo_cestos || []))
            for (const ci of (ac.cestos?.cesto_itens || []))
              comps.push({ pedido_item_id: pi.id, item_id: ci.item_id, quantidade: ci.quantidade * ac.quantidade, custo_unitario: ci.itens?.custo ?? ci.custo_unitario, tipo_comp: 'item' })
        } else if (item.tipo === 'personalizado') {
          for (const s of (item.bItens || []))
            comps.push({ pedido_item_id: pi.id, item_id: s.item.id, quantidade: s.qty, custo_unitario: s.item.custo, tipo_comp: 'item' })
          for (const s of (item.bCestos || []))
            for (const ci of (s.cesto?.cesto_itens || []))
              comps.push({ pedido_item_id: pi.id, item_id: ci.item_id, quantidade: ci.quantidade * s.qty, custo_unitario: ci.itens?.custo ?? ci.custo_unitario, tipo_comp: 'item' })
        }
        if (comps.length > 0) {
          const { error: compsError } = await supabase.from('pedido_item_composicao').insert(comps)
          if (compsError) {
            await supabase.from('pedidos').delete().eq('id', pedido.id)
            show('Erro ao salvar composicao do pedido')
            setSaving(false)
            return
          }
        }
      }
    }

    if (status === 'confirmado') {
      const baixa = await baixarEstoque(itensPedido, pedido.id)
      if (!baixa.ok) {
        await supabase.from('pedidos').delete().eq('id', pedido.id)
        show('Erro ao baixar estoque. Pedido nao confirmado.')
        setSaving(false)
        return
      }

      const { error: statusError } = await supabase.from('pedidos').update({ status: 'confirmado' }).eq('id', pedido.id)
      if (statusError) {
        await reverterBaixaEstoque(baixa)
        await supabase.from('pedidos').delete().eq('id', pedido.id)
        show('Erro ao confirmar pedido. Estoque revertido.')
        setSaving(false)
        return
      }
    }

    show(status === 'confirmado' ? '✅ Pedido confirmado!' : '✅ Orçamento salvo!')
    setSaving(false)
    resetNovo()
    setView('lista')
    fetchTudo()
  }

  async function baixarEstoque(itensLocal, pedidoId = null) {
    // agrupa por itemId para fazer uma única atualização por item
    const mapa = {}
    for (const { itemId, qty: q } of baixasDeItensLocal(itensLocal)) {
      mapa[itemId] = (mapa[itemId] || 0) + q
    }
    const alterados = []
    const movimentosCriados = []
    for (const [itemId, qtdTotal] of Object.entries(mapa)) {
      const { data: itemAtual } = await supabase.from('itens').select('estoque').eq('id', itemId).single()
      if (!itemAtual) {
        await reverterBaixaEstoque({ alterados, movimentosCriados })
        return { ok: false }
      }
      const estoqueAnterior = parseFloat(itemAtual.estoque) || 0
      const novoEstoque = estoqueAnterior - qtdTotal
      const { error: estoqueError } = await supabase.from('itens').update({ estoque: novoEstoque }).eq('id', itemId)
      if (estoqueError) {
        await reverterBaixaEstoque({ alterados, movimentosCriados })
        return { ok: false }
      }
      alterados.push({ itemId, estoqueAnterior })

      const { data: movimento, error: movimentoError } = await supabase.from('movimentos').insert([{
        pedido_id: pedidoId,
        item_id: itemId,
        tipo: 'saida',
        quantidade: qtdTotal,
        custo_unitario: 0,
      }]).select('id').single()
      if (movimentoError || !movimento) {
        await reverterBaixaEstoque({ alterados, movimentosCriados })
        return { ok: false }
      }
      movimentosCriados.push(movimento.id)
    }
    return { ok: true, alterados, movimentosCriados }
  }

  async function reverterBaixaEstoque(baixa) {
    for (const id of (baixa.movimentosCriados || [])) {
      await supabase.from('movimentos').delete().eq('id', id)
    }
    for (const item of [...(baixa.alterados || [])].reverse()) {
      await supabase.from('itens').update({ estoque: item.estoqueAnterior }).eq('id', item.itemId)
    }
  }

  function itensDoPedidoSalvo(pedido) {
    return (pedido.pedido_itens || []).map(pi => ({
      tipo: pi.tipo,
      qty: pi.quantidade,
      // usa snapshot salvo no momento do pedido — nunca o arranjo atual
      bItens: (pi.pedido_item_composicao || [])
        .filter(c => c.tipo_comp === 'item' && c.itens)
        .map(c => ({ item: c.itens, qty: c.quantidade })),
      bCestos: (pi.pedido_item_composicao || [])
        .filter(c => c.tipo_comp === 'cesto')
        .map(c => ({ cesto: cestos.find(cs => cs.id === c.cesto_id) || c.cestos, qty: c.quantidade })),
    }))
  }

  async function confirmarExistente(pedido, forcar = false) {
    const itensLocal = itensDoPedidoSalvo(pedido)
    if (!forcar) {
      const falta = checarEstoque(itensLocal)
      if (falta.length > 0) {
        setAvisoEstoque({ falta, onConfirmar: () => { setAvisoEstoque(null); confirmarExistente(pedido, true) } })
        setConfirm(null)
        return
      }
    }
    const baixa = await baixarEstoque(itensLocal, pedido.id)
    if (!baixa.ok) {
      show('Erro ao baixar estoque. Pedido nao confirmado.')
      setConfirm(null)
      return
    }

    const { error } = await supabase.from('pedidos').update({ status: 'confirmado' }).eq('id', pedido.id)
    if (error) {
      await reverterBaixaEstoque(baixa)
      show('Erro ao confirmar pedido. Estoque revertido.')
      setConfirm(null)
      return
    }
    show('✅ Pedido confirmado!')
    fetchTudo()
    setViewPedido(prev => prev ? { ...prev, status: 'confirmado' } : null)
    setConfirm(null)
  }

  async function atualizarStatus(pedido, novoStatus) {
    const { error } = await supabase.from('pedidos').update({ status: novoStatus }).eq('id', pedido.id)
    if (error) { show('Erro ao atualizar status'); return }
    show('Status atualizado!')
    fetchTudo()
    setViewPedido(prev => prev ? { ...prev, status: novoStatus } : null)
  }

  async function deletarPedido(pedido) {
    const estoquesAnteriores = []
    if (['confirmado', 'entregue'].includes(pedido.status)) {
      const mapa = {}
      for (const { itemId, qty: q } of baixasDeItensLocal(itensDoPedidoSalvo(pedido))) {
        mapa[itemId] = (mapa[itemId] || 0) + q
      }

      for (const [itemId, qtdTotal] of Object.entries(mapa)) {
        const { data: itemAtual } = await supabase.from('itens').select('estoque').eq('id', itemId).single()
        if (!itemAtual) continue
        estoquesAnteriores.push({ itemId, estoqueAnterior: parseFloat(itemAtual.estoque) || 0 })
        const novoEstoque = (parseFloat(itemAtual.estoque) || 0) + qtdTotal
        const { error: estoqueError } = await supabase.from('itens').update({ estoque: novoEstoque }).eq('id', itemId)
        if (estoqueError) {
          for (const item of estoquesAnteriores.reverse()) {
            await supabase.from('itens').update({ estoque: item.estoqueAnterior }).eq('id', item.itemId)
          }
          show('Erro ao devolver estoque')
          return
        }
      }

      const { error: statusError } = await supabase.from('pedidos').update({ status: 'orcamento' }).eq('id', pedido.id)
      if (statusError) {
        for (const item of estoquesAnteriores.reverse()) {
          await supabase.from('itens').update({ estoque: item.estoqueAnterior }).eq('id', item.itemId)
        }
        show('Erro ao cancelar venda. Estoque revertido.')
        return
      }

      const { error: movError } = await supabase.from('movimentos').delete().eq('pedido_id', pedido.id)
      if (movError) {
        for (const item of estoquesAnteriores.reverse()) {
          await supabase.from('itens').update({ estoque: item.estoqueAnterior }).eq('id', item.itemId)
        }
        await supabase.from('pedidos').update({ status: pedido.status }).eq('id', pedido.id)
        show('Erro ao remover movimentos. Estoque revertido.')
        return
      }
    }

    const { error } = await supabase.from('pedidos').delete().eq('id', pedido.id)
    if (error) { show('Erro ao remover pedido'); return }
    show('Pedido removido')
    setViewPedido(null)
    setConfirm(null)
    setView('lista')
    fetchTudo()
  }

  // ---- FICHA ----
  function gerarFicha(p) {
    const itensTxt = (p.pedido_itens || [])
      .map(pi => `• ${pi.quantidade}x ${pi.nome} — R$${(parseFloat(pi.preco_venda) * pi.quantidade).toFixed(2)}`)
      .join('\n')
    const sub = (p.pedido_itens || []).reduce((s, pi) => s + parseFloat(pi.preco_venda) * pi.quantidade, 0)
    const frt = parseFloat(p.frete || 0)
    const dsc = parseFloat(p.desconto || 0)
    const tot = parseFloat(p.total || 0)

    let t = `*Pedido — Flor de Sarah* 🌸\n`
    if (p.cliente_nome) t += `Cliente: ${p.cliente_nome}\n`
    if (p.data_entrega) t += `Entrega: ${fmtData(p.data_entrega + 'T12:00:00')}\n`
    t += `Pagamento: ${PAG_LABEL[p.pagamento] || p.pagamento}\n\n`
    t += `*Itens:*\n${itensTxt}\n\n`
    if (dsc > 0 || frt > 0) {
      t += `Subtotal: R$${sub.toFixed(2)}\n`
      if (dsc > 0) t += `Desconto: -R$${dsc.toFixed(2)}\n`
      if (frt > 0) t += `Frete: R$${frt.toFixed(2)}\n`
    }
    t += `*Total: R$${tot.toFixed(2)}*`
    if (p.recado) t += `\n\nRecado: "${p.recado}"`
    if (p.observacao) t += `\nObs: ${p.observacao}`
    return t
  }

  function whatsLink(tel, texto) {
    const n = (tel || '').replace(/\D/g, '')
    const num = n.startsWith('55') ? n : '55' + n
    return `https://wa.me/${num}${texto ? '?text=' + encodeURIComponent(texto) : ''}`
  }

  const pedidosFiltrados = pedidos.filter(p => filtroStatus === '' || p.status === filtroStatus)
  const categorias = [...new Set(itens.map(i => i.categoria))]

  const Toast = () => <div className={`toast ${visible ? 'show' : ''}`}>{msg}</div>

  const AvisoEstoque = () => avisoEstoque ? (
    <div className="modal-overlay" onClick={() => setAvisoEstoque(null)}>
      <div className="modal" style={{ paddingBottom: 20 }} onClick={e => e.stopPropagation()}>
        <div className="modal-handle" />
        <div className="modal-title" style={{ color: '#c2185b' }}>⚠️ Estoque insuficiente</div>
        <p style={{ fontSize: 13, color: '#666', marginBottom: 12 }}>Os itens abaixo estão com estoque abaixo do necessário:</p>
        {avisoEstoque.falta.map(f => (
          <div key={f.nome} style={{ padding: '8px 12px', marginBottom: 6, borderRadius: 8, background: '#fce4ec', fontSize: 13 }}>
            <span style={{ fontWeight: 600, color: '#c2185b' }}>{f.nome}</span>
            <span style={{ color: '#888', marginLeft: 8 }}>disponível: {f.disponivel} · necessário: {f.necessario.toFixed(2)}</span>
          </div>
        ))}
        <p style={{ fontSize: 12, color: '#999', marginTop: 12, marginBottom: 20 }}>
          Você pode confirmar mesmo assim — o estoque ficará negativo. Depois lance uma entrada para acertar.
        </p>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-ghost" style={{ flex: 1 }} onClick={() => setAvisoEstoque(null)}>Cancelar</button>
          <button className="btn btn-primary" style={{ flex: 1 }} onClick={avisoEstoque.onConfirmar}>Confirmar mesmo assim</button>
        </div>
      </div>
    </div>
  ) : null

  const ModalConfirm = () => confirm ? (
    <div className="modal-overlay" onClick={() => setConfirm(null)}>
      <div className="modal" style={{ paddingBottom: 20 }} onClick={e => e.stopPropagation()}>
        <div className="modal-handle" />
        <p style={{ fontSize: 15, marginBottom: 20, textAlign: 'center', fontWeight: 600 }}>{confirm.msg}</p>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-ghost" style={{ flex: 1 }} onClick={() => setConfirm(null)}>Cancelar</button>
          <button className={`btn ${confirm.danger ? 'btn-danger' : 'btn-primary'}`} style={{ flex: 1 }} onClick={confirm.fn}>
            {confirm.label || 'Confirmar'}
          </button>
        </div>
      </div>
    </div>
  ) : null

  if (loading) return <div className="spinner">Carregando...</div>

  // ===== LISTA =====
  if (view === 'lista') return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h1 className="page-title" style={{ margin: 0 }}>Vendas</h1>
        <button className="btn btn-primary btn-sm" onClick={() => { resetNovo(); setView('novo') }}>+ Novo pedido</button>
      </div>

      <div style={{ display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap' }}>
        {['', 'orcamento', 'confirmado', 'entregue'].map(s => (
          <button key={s} className={`btn btn-sm ${filtroStatus === s ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setFiltroStatus(s)}>
            {s === '' ? 'Todos' : STATUS_LABEL[s]}
          </button>
        ))}
      </div>

      {pedidosFiltrados.length === 0 && (
        <div className="empty">
          <div className="empty-icon">🛒</div>
          <p>Nenhum pedido ainda.</p>
        </div>
      )}

      {pedidosFiltrados.map(p => (
        <div key={p.id} className="card" style={{ marginBottom: 10, padding: '12px 16px', cursor: 'pointer' }}
          onClick={() => { setViewPedido(p); setView('detalhe') }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: 15 }}>{p.cliente_nome || 'Cliente não informado'}</div>
              <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>
                {p.data_entrega ? `Entrega: ${fmtData(p.data_entrega + 'T12:00:00')}` : fmtData(p.created_at)}
                {' · '}{(p.pedido_itens?.length || 0)} {(p.pedido_itens?.length || 0) === 1 ? 'item' : 'itens'}
                {' · '}{PAG_LABEL[p.pagamento]}
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--pink-dark)' }}>R${parseFloat(p.total).toFixed(2)}</div>
              <div style={{ fontSize: 11, fontWeight: 600, color: STATUS_COLOR[p.status], marginTop: 2 }}>{STATUS_LABEL[p.status]}</div>
            </div>
          </div>
        </div>
      ))}
      <Toast />
    </div>
  )

  // ===== DETALHE =====
  if (view === 'detalhe' && viewPedido) {
    const p = pedidos.find(x => x.id === viewPedido.id) || viewPedido
    const fichaTexto = gerarFicha(p)

    return (
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
          <button className="btn btn-ghost btn-sm" onClick={() => { setView('lista'); setViewPedido(null) }}>← Voltar</button>
          <h1 className="page-title" style={{ margin: 0, flex: 1 }}>{p.cliente_nome || 'Pedido'}</h1>
          <div style={{ fontSize: 11, fontWeight: 600, color: STATUS_COLOR[p.status], padding: '4px 10px', borderRadius: 20, border: `1px solid ${STATUS_COLOR[p.status]}` }}>
            {STATUS_LABEL[p.status]}
          </div>
        </div>

        <div className="cost-box" style={{ marginBottom: 12 }}>
          {p.cliente_whats && (
            <div className="cost-row">
              <span>WhatsApp</span>
              <a href={whatsLink(p.cliente_whats)} target="_blank" rel="noopener noreferrer"
                className="btn btn-ghost btn-sm" style={{ padding: '2px 8px' }}>💬 {p.cliente_whats}</a>
            </div>
          )}
          {p.data_entrega && <div className="cost-row"><span>Entrega</span><span>{fmtData(p.data_entrega + 'T12:00:00')}</span></div>}
          <div className="cost-row"><span>Pagamento</span><span>{PAG_LABEL[p.pagamento] || p.pagamento}</span></div>
          <div className="cost-row"><span>Data do pedido</span><span>{fmtData(p.created_at)}</span></div>
        </div>

        <div className="section-label">Produtos</div>
        <div className="card" style={{ marginBottom: 12 }}>
          {(p.pedido_itens || []).map(pi => (
            <div key={pi.id} style={{ padding: '10px 0', borderBottom: '1px solid var(--border)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>{pi.quantidade}x {pi.nome}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                    {pi.tipo === 'personalizado' ? 'Personalizado' : 'Catálogo'} · R${parseFloat(pi.preco_venda).toFixed(2)}/un
                  </div>
                  {pi.tipo === 'personalizado' && (pi.pedido_item_composicao || []).filter(c => c.tipo_comp === 'item').length > 0 && (
                    <div style={{ fontSize: 11, color: '#aaa', marginTop: 2 }}>
                      {(pi.pedido_item_composicao || []).filter(c => c.tipo_comp === 'item').map(c => `${c.quantidade}x ${c.itens?.nome}`).join(' · ')}
                    </div>
                  )}
                </div>
                <div style={{ fontWeight: 700, color: 'var(--pink-dark)' }}>
                  R${(parseFloat(pi.preco_venda) * pi.quantidade).toFixed(2)}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="cost-box" style={{ marginBottom: 12 }}>
          {(() => {
            const sub = (p.pedido_itens || []).reduce((s, pi) => s + parseFloat(pi.preco_venda) * pi.quantidade, 0)
            const frt = parseFloat(p.frete || 0)
            const dsc = parseFloat(p.desconto || 0)
            return <>
              <div className="cost-row"><span>Subtotal</span><span>R${sub.toFixed(2)}</span></div>
              {frt > 0 && <div className="cost-row"><span>Frete</span><span>R${frt.toFixed(2)}</span></div>}
              {dsc > 0 && <div className="cost-row"><span>Desconto</span><span>-R${dsc.toFixed(2)}</span></div>}
              <div className="cost-row total"><span>Total</span><span>R${parseFloat(p.total).toFixed(2)}</span></div>
            </>
          })()}
        </div>

        {p.recado && (
          <div className="card" style={{ marginBottom: 12, background: '#fce4ec', padding: '10px 14px' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#c2185b', marginBottom: 4, textTransform: 'uppercase' }}>Recado no cartão</div>
            <div style={{ fontSize: 14, fontStyle: 'italic' }}>"{p.recado}"</div>
          </div>
        )}

        {p.observacao && (
          <div className="card" style={{ marginBottom: 12, padding: '10px 14px' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 4, textTransform: 'uppercase' }}>Observações</div>
            <div style={{ fontSize: 14 }}>{p.observacao}</div>
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
          {p.status === 'orcamento' && (
            <button className="btn btn-primary" style={{ flex: 1 }}
              onClick={() => setConfirm({ msg: 'Confirmar pedido e baixar estoque?', label: 'Confirmar', fn: () => confirmarExistente(p) })}>
              Confirmar pedido
            </button>
          )}
          {p.status === 'confirmado' && (
            <button className="btn btn-primary" style={{ flex: 1 }} onClick={() => atualizarStatus(p, 'entregue')}>
              Marcar como entregue
            </button>
          )}
          <button className="btn btn-ghost" style={{ flex: 1 }} onClick={() => setFichaOpen(true)}>📋 Ficha</button>
        </div>
        <button className="btn btn-danger" style={{ width: '100%' }}
          onClick={() => setConfirm({ msg: `Remover pedido de "${p.cliente_nome || 'cliente'}"?`, danger: true, label: 'Remover', fn: () => deletarPedido(p) })}>
          Remover pedido
        </button>

        {fichaOpen && (
          <div className="modal-overlay" onClick={() => setFichaOpen(false)}>
            <div className="modal" onClick={e => e.stopPropagation()}>
              <div className="modal-handle" />
              <div className="modal-title">Ficha do pedido</div>
              <div style={{ background: 'var(--bg)', borderRadius: 10, padding: 12, fontSize: 13, whiteSpace: 'pre-wrap', fontFamily: 'monospace', marginBottom: 16, maxHeight: 280, overflowY: 'auto', lineHeight: 1.6 }}>
                {fichaTexto}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-primary" style={{ flex: 1 }}
                  onClick={() => { navigator.clipboard.writeText(fichaTexto); show('Copiado!') }}>
                  📋 Copiar
                </button>
                {p.cliente_whats && (
                  <a href={whatsLink(p.cliente_whats, fichaTexto)} target="_blank" rel="noopener noreferrer"
                    className="btn btn-ghost" style={{ flex: 1, textAlign: 'center', textDecoration: 'none' }}>
                    💬 Enviar
                  </a>
                )}
              </div>
            </div>
          </div>
        )}

        <AvisoEstoque />
        <ModalConfirm />
        <Toast />
      </div>
    )
  }

  // ===== BUILDER (item personalizado) =====
  if (editingItem !== null) {
    const { total: bTotal, montagem: bMontagem } = builderCusto
    const precoNum = pp(bPreco)

    return (
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
          <button className="btn btn-ghost btn-sm" onClick={() => setEditingItem(null)}>← Cancelar</button>
          <h1 className="page-title" style={{ margin: 0 }}>
            {typeof editingItem === 'string' && editingItem !== 'novo' ? 'Editar produto do pedido' : 'Item personalizado'}
          </h1>
        </div>

        <div className="field">
          <label>Nome do item</label>
          <input placeholder="Ex: Buquê especial da noiva" value={bNome} onChange={e => setBNome(e.target.value)} autoFocus />
        </div>

        {cestos.length > 0 && (
          <>
            <div className="section-label">Cestos</div>
            {cestos.map(cesto => {
              const sel = bCestos.find(s => s.cesto.id === cesto.id)
              return (
                <div key={cesto.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', borderRadius: 8, marginBottom: 2, background: sel ? 'var(--pink-light)' : 'transparent', transition: 'background 0.15s' }}>
                  <input type="checkbox" checked={!!sel} onChange={() => toggleBCesto(cesto)} style={{ width: 18, height: 18, accentColor: 'var(--pink)', cursor: 'pointer' }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 14, fontWeight: sel ? 600 : 500 }}>🧺 {cesto.nome}</div>
                    <div style={{ fontSize: 12, color: '#888' }}>R${parseFloat(cesto.custo).toFixed(2)}</div>
                  </div>
                  {sel && (
                    <div className="qty-ctrl">
                      <button className="qty-btn" onClick={() => setBCestoQty(cesto.id, sel.qty - 1)}>−</button>
                      <span className="qty-num">{sel.qty}</span>
                      <button className="qty-btn" onClick={() => setBCestoQty(cesto.id, sel.qty + 1)}>+</button>
                    </div>
                  )}
                  {sel && <div style={{ fontSize: 12, color: 'var(--pink-dark)', fontWeight: 600, minWidth: 52, textAlign: 'right' }}>R${(cesto.custo * sel.qty).toFixed(2)}</div>}
                </div>
              )
            })}
          </>
        )}

        <div className="section-label">Itens avulsos</div>
        <div className="row" style={{ marginBottom: 12 }}>
          <div className="field" style={{ marginBottom: 0 }}>
            <input placeholder="Buscar..." value={bBusca} onChange={e => setBBusca(e.target.value)} />
          </div>
          <div className="field" style={{ marginBottom: 0 }}>
            <select value={bCat} onChange={e => setBCat(e.target.value)}>
              <option value="">Todas</option>
              {categorias.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        </div>

        {categorias.filter(cat => bCat === '' || cat === bCat).map(cat => {
          const lista = itens.filter(i => i.categoria === cat && i.nome.toLowerCase().includes(bBusca.toLowerCase()))
          if (lista.length === 0) return null
          return (
            <div key={cat}>
              <div style={{ fontSize: 11, color: '#aaa', fontWeight: 600, textTransform: 'uppercase', margin: '10px 0 4px' }}>{cat}</div>
              {lista.map(item => {
                const sel = bItens.find(s => s.item.id === item.id)
                return (
                  <div key={item.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', borderRadius: 8, marginBottom: 2, background: sel ? 'var(--pink-light)' : 'transparent', transition: 'background 0.15s' }}>
                    <input type="checkbox" checked={!!sel} onChange={() => toggleBItem(item)} style={{ width: 18, height: 18, accentColor: 'var(--pink)', cursor: 'pointer' }} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 14, fontWeight: sel ? 600 : 500 }}>{item.nome}</div>
                      <div style={{ fontSize: 12, color: '#888' }}>R${parseFloat(item.custo).toFixed(2)} / {item.unidade}</div>
                    </div>
                    {sel && (
                      <div className="qty-ctrl">
                        <button className="qty-btn" onClick={() => setBItemQty(item.id, sel.qty - 1)}>−</button>
                        <span className="qty-num">{sel.qty}</span>
                        <button className="qty-btn" onClick={() => setBItemQty(item.id, sel.qty + 1)}>+</button>
                      </div>
                    )}
                    {sel && <div style={{ fontSize: 12, color: 'var(--pink-dark)', fontWeight: 600, minWidth: 52, textAlign: 'right' }}>R${(item.custo * sel.qty).toFixed(2)}</div>}
                  </div>
                )
              })}
            </div>
          )
        })}

        {(bItens.length > 0 || bCestos.length > 0) && (
          <div className="cost-box" style={{ marginTop: 16 }}>
            {bCestos.map(s => <div className="cost-row" key={s.cesto.id}><span>🧺 {s.qty}x {s.cesto.nome}</span><span>R${(s.cesto.custo * s.qty).toFixed(2)}</span></div>)}
            {bItens.map(s => <div className="cost-row" key={s.item.id}><span>{s.qty}x {s.item.nome}</span><span>R${(s.item.custo * s.qty).toFixed(2)}</span></div>)}
            <div className="cost-row" style={{ color: '#888', borderTop: '1px solid #f0e0e8', marginTop: 4, paddingTop: 4 }}>
              <span>Montagem</span><span>R${bMontagem.toFixed(2)}</span>
            </div>
            <div className="cost-row total"><span>Custo total</span><span>R${bTotal.toFixed(2)}</span></div>
          </div>
        )}

        <div className="section-label" style={{ marginTop: 16 }}>Preço de venda</div>
        <div className="row" style={{ marginBottom: 8, alignItems: 'flex-end' }}>
          <div className="field" style={{ marginBottom: 0 }}>
            <label>Valor (R$)</label>
            <input type="text" inputMode="decimal" placeholder="0,00" value={bPreco}
              onChange={e => setBPreco(e.target.value)}
              onFocus={e => e.target.select()}
              onBlur={() => { const v = pp(bPreco); if (v > 0) setBPreco(v.toFixed(2)) }}
            />
            {bTotal > 0 && <div style={{ fontSize: 11, color: 'var(--pink-dark)', marginTop: 4 }}>mín. 40%: R${(bTotal / 0.6).toFixed(2)}</div>}
          </div>
          {bTotal > 0 && precoNum > 0 && (
            <div style={{ display: 'flex', gap: 8, paddingBottom: 2 }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 10, color: '#aaa', fontWeight: 600, textTransform: 'uppercase', marginBottom: 2 }}>Margem</div>
                <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--pink-dark)' }}>{(((precoNum - bTotal) / precoNum) * 100).toFixed(1)}%</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 10, color: '#aaa', fontWeight: 600, textTransform: 'uppercase', marginBottom: 2 }}>Markup</div>
                <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--green)' }}>{(((precoNum - bTotal) / bTotal) * 100).toFixed(1)}%</div>
              </div>
            </div>
          )}
        </div>

        <button className="btn btn-primary" style={{ width: '100%', marginTop: 8 }} onClick={salvarBuilder}>
          Adicionar ao pedido
        </button>
        <Toast />
      </div>
    )
  }

  // ===== NOVO — STEP 1: PRODUTOS =====
  if (step === 'produtos') {
    const arrFiltrados = arranjos.filter(a => a.nome.toLowerCase().includes(buscaArr.toLowerCase()))

    return (
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
          <button className="btn btn-ghost btn-sm" onClick={() => setView('lista')}>← Cancelar</button>
          <h1 className="page-title" style={{ margin: 0, flex: 1 }}>Novo pedido</h1>
          <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>1 / 3</div>
        </div>

        {itensPedido.length > 0 && (
          <div className="cost-box" style={{ marginBottom: 16 }}>
            {itensPedido.map(item => (
              <div key={item.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.nome}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{item.tipo === 'personalizado' ? 'Personalizado' : 'Catálogo'}</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                  <div className="qty-ctrl">
                    <button className="qty-btn" onClick={() => updateItem(item.id, 'qty', Math.max(1, (item.qty || 1) - 1))}>−</button>
                    <span className="qty-num">{item.qty || 1}</span>
                    <button className="qty-btn" onClick={() => updateItem(item.id, 'qty', (item.qty || 1) + 1)}>+</button>
                  </div>
                  <input
                    type="text" inputMode="decimal"
                    style={{ width: 72, textAlign: 'right', border: '1px solid var(--border)', borderRadius: 6, padding: '4px 6px', fontSize: 13 }}
                    value={item.precoInput}
                    onChange={e => updateItem(item.id, 'precoInput', e.target.value)}
                    onBlur={() => { const v = pp(item.precoInput); if (v > 0) updateItem(item.id, 'precoInput', v.toFixed(2)) }}
                  />
                  <button className="btn btn-ghost btn-sm" onClick={() => abrirBuilder(item.id)}>editar</button>
                  <button className="btn btn-danger btn-sm" onClick={() => setItensPedido(prev => prev.filter(i => i.id !== item.id))}>×</button>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="section-label">Adicionar produto</div>

        <button className="btn btn-ghost" style={{ width: '100%', marginBottom: 12, justifyContent: 'flex-start', gap: 8 }}
          onClick={() => abrirBuilder()}>
          ✨ Criar item personalizado
        </button>

        <input placeholder="🔍 Buscar no catálogo..." value={buscaArr}
          onChange={e => setBuscaArr(e.target.value)} style={{ width: '100%', marginBottom: 8 }} />

        {arrFiltrados.length === 0 && buscaArr && (
          <div style={{ textAlign: 'center', color: 'var(--text-secondary)', fontSize: 14, padding: 16 }}>Nenhum produto encontrado</div>
        )}

        {arrFiltrados.length > 0 && (
          <div className="card" style={{ padding: '4px 16px' }}>
            {arrFiltrados.map(arr => (
              <div key={arr.id} className="list-item">
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {arr.foto_url && <img src={arr.foto_url} alt="" style={{ width: 36, height: 36, borderRadius: 6, objectFit: 'cover' }} />}
                  <div>
                    <div className="item-name">{arr.nome}</div>
                    <div className="item-sub">R${parseFloat(arr.preco_venda).toFixed(2)}</div>
                  </div>
                </div>
                <button className="btn btn-primary btn-sm" onClick={() => addCatalogo(arr)}>+ Add</button>
              </div>
            ))}
          </div>
        )}

        {itensPedido.length > 0 && (
          <button className="btn btn-primary" style={{ marginTop: 16, width: '100%' }} onClick={() => setStep('cliente')}>
            Continuar ({itensPedido.length} {itensPedido.length === 1 ? 'item' : 'itens'}) →
          </button>
        )}
        <Toast />
      </div>
    )
  }

  // ===== NOVO — STEP 2: CLIENTE =====
  if (step === 'cliente') return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
        <button className="btn btn-ghost btn-sm" onClick={() => setStep('produtos')}>← Voltar</button>
        <h1 className="page-title" style={{ margin: 0, flex: 1 }}>Cliente e entrega</h1>
        <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>2 / 3</div>
      </div>

      <div className="row">
        <div className="field">
          <label>Nome do cliente</label>
          <input placeholder="Ex: Maria Silva" value={cliNome} onChange={e => setCliNome(e.target.value)} autoFocus />
        </div>
        <div className="field">
          <label>WhatsApp</label>
          <input placeholder="(11) 99999-9999" type="tel" value={cliWhats} onChange={e => setCliWhats(e.target.value)} />
        </div>
      </div>

      <div className="row">
        <div className="field">
          <label>Data de entrega</label>
          <input type="date" value={dataEntrega} onChange={e => setDataEntrega(e.target.value)} />
        </div>
        <div className="field">
          <label>Pagamento</label>
          <select value={pagamento} onChange={e => setPagamento(e.target.value)}>
            <option value="pix">PIX</option>
            <option value="dinheiro">Dinheiro</option>
            <option value="cartao">Cartão</option>
          </select>
        </div>
      </div>

      <div className="row">
        <div className="field">
          <label>Frete (R$)</label>
          <input type="text" inputMode="decimal" placeholder="0,00" value={frete} onChange={e => setFrete(e.target.value)} />
        </div>
        <div className="field">
          <label>Desconto (R$)</label>
          <input type="text" inputMode="decimal" placeholder="0,00" value={desconto} onChange={e => setDesconto(e.target.value)} />
        </div>
      </div>

      <div className="field">
        <label>Recado no cartão</label>
        <input placeholder='Ex: "Feliz aniversário, com amor!"' value={recado} onChange={e => setRecado(e.target.value)} />
      </div>

      <div className="field">
        <label>Observações</label>
        <input placeholder="Cor preferida, restrições, detalhes..." value={observacao} onChange={e => setObservacao(e.target.value)} />
      </div>

      <button className="btn btn-primary" style={{ width: '100%', marginTop: 8 }} onClick={() => setStep('resumo')}>
        Ver resumo →
      </button>
      <Toast />
    </div>
  )

  // ===== NOVO — STEP 3: RESUMO =====
  if (step === 'resumo') {
    const lucro = itensPedido.reduce((s, i) => s + (pp(i.precoInput) - i.custoTotal) * (i.qty || 1), 0)

    return (
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
          <button className="btn btn-ghost btn-sm" onClick={() => setStep('cliente')}>← Voltar</button>
          <h1 className="page-title" style={{ margin: 0, flex: 1 }}>Resumo</h1>
          <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>3 / 3</div>
        </div>

        {(cliNome || dataEntrega) && (
          <div className="card" style={{ marginBottom: 12, padding: '10px 16px' }}>
            {cliNome && <div style={{ fontWeight: 700, fontSize: 15 }}>{cliNome}</div>}
            {dataEntrega && <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Entrega: {fmtData(dataEntrega + 'T12:00:00')}</div>}
            <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{PAG_LABEL[pagamento]}</div>
          </div>
        )}

        <div className="cost-box" style={{ marginBottom: 12 }}>
          {itensPedido.map(item => (
            <div key={item.id} className="cost-row">
              <span>{item.qty || 1}x {item.nome}</span>
              <span>R${(pp(item.precoInput) * (item.qty || 1)).toFixed(2)}</span>
            </div>
          ))}
          {freteVal > 0 && <div className="cost-row"><span>Frete</span><span>R${freteVal.toFixed(2)}</span></div>}
          {descontoVal > 0 && <div className="cost-row"><span>Desconto</span><span>-R${descontoVal.toFixed(2)}</span></div>}
          <div className="cost-row total"><span>Total</span><span>R${totalPedido.toFixed(2)}</span></div>
        </div>

        <div className="cost-box" style={{ marginBottom: 12, background: '#f1f8e9' }}>
          <div className="cost-row"><span style={{ fontSize: 13 }}>Custo estimado</span><span>R${custoTotalPedido.toFixed(2)}</span></div>
          <div className="cost-row">
            <span style={{ fontSize: 13 }}>Lucro estimado</span>
            <span style={{ color: lucro >= 0 ? 'var(--green)' : '#f44336', fontWeight: 700 }}>R${lucro.toFixed(2)}</span>
          </div>
          <div className="cost-row">
            <span style={{ fontSize: 13 }}>Margem</span>
            <span style={{ color: 'var(--green)', fontWeight: 700 }}>
              {totalPedido > 0 ? (((totalPedido - custoTotalPedido) / totalPedido) * 100).toFixed(1) : 0}%
            </span>
          </div>
        </div>

        {recado && (
          <div className="card" style={{ marginBottom: 12, background: '#fce4ec', padding: '10px 14px' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#c2185b', marginBottom: 2, textTransform: 'uppercase' }}>Recado</div>
            <div style={{ fontSize: 14, fontStyle: 'italic' }}>"{recado}"</div>
          </div>
        )}

        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-ghost" style={{ flex: 1 }} onClick={() => salvarPedido('orcamento')} disabled={saving}>
            {saving ? '...' : 'Salvar orçamento'}
          </button>
          <button className="btn btn-primary" style={{ flex: 2 }} onClick={() => salvarPedido('confirmado')} disabled={saving}>
            {saving ? 'Salvando...' : 'Confirmar pedido'}
          </button>
        </div>
        <AvisoEstoque />
        <Toast />
      </div>
    )
  }

  return null
}
